import React, { useState, useRef, useEffect } from "react"
import PropTypes from "prop-types"
import { DragDropContext } from "@hello-pangea/dnd"
import { useDeepCompareEffect } from "use-deep-compare"

// Components
import PipelineColumn from "./PipelineColumn/PipelineColumn"

// Style
import { PipelineContainer } from "./index.style"

// Utils
import {
  getPipelineColumns,
  getColumnsFillWithItems,
  getColumnsWithMovingItem,
  getColumnsWithReorderItem,
  getDestinationColumnName,
  getMovingElement,
  getLastMoves,
  widthColumn,
} from "./pipeline.utils"

const PipelineComponent = ({
  dataChart,
  valueData,
  categoryData,
  pipelineColumns,
  onMoveElements,
}) => {
  //
  const [columns, setColumns] = useState([])

  const updateWidgetDataTimeout = useRef(null)
  const movesRef = useRef([])
  const onMoveElementsRef = useRef()

  useEffect(() => {
    // We need to listen onMoveElements and attribute to our ref because the dataRelatedProps
    // Including function "onMoveElements" are not complete at first render
    onMoveElementsRef.current = onMoveElements
  }, [onMoveElements])

  // Initialize columns with their items
  useDeepCompareEffect(() => {
    const initialColumns = getPipelineColumns(pipelineColumns, dataChart, categoryData)
    const columnsWithItems = getColumnsFillWithItems(
      initialColumns,
      dataChart,
      categoryData,
      valueData
    )
    setColumns(columnsWithItems)
  }, [dataChart, valueData, categoryData, pipelineColumns])

  // We sent moves every 4 seconds for update dataSource
  useEffect(() => {
    return () => clearTimeout(updateWidgetDataTimeout.current)
  }, [])

  useEffect(() => {
    if (movesRef.current.length === 0) return
    if (updateWidgetDataTimeout.current) {
      clearTimeout(updateWidgetDataTimeout.current)
    }
    updateWidgetDataTimeout.current = setTimeout(function () {
      const lastMoves = getLastMoves(movesRef.current)
      onMoveElements(lastMoves)
      movesRef.current = []
    }, 4000)
  }, [movesRef.current.length])

  // We use refs to get access to the right function and values
  useEffect(() => {
    return () => {
      if (movesRef.current.length) {
        const lastMoves = getLastMoves(movesRef.current)
        onMoveElementsRef.current(lastMoves)
      }
    }
  }, [])

  // Function to move card
  const onDragEnd = result => {
    const { source, destination } = result
    if (!destination) return
    const isSameColumn = source?.droppableId === destination?.droppableId

    let newColumns = []
    if (isSameColumn) newColumns = getColumnsWithReorderItem(columns, source, destination)
    else newColumns = getColumnsWithMovingItem(columns, source, destination)
    const movingElement = getMovingElement(columns, destination)
    const destinationColumnName = getDestinationColumnName(columns, destination)

    setColumns(newColumns)

    const previousMoves = [...movesRef.current]
    const updatedMoves = [...previousMoves, { movingElement, destinationColumnName }]
    movesRef.current = updatedMoves
  }

  return (
    <PipelineContainer role="pipelineContainer">
      <DragDropContext onDragEnd={onDragEnd}>
        {Array.isArray(columns) &&
          columns.map((column, index) => (
            <PipelineColumn
              key={index}
              index={index}
              name={column?.columnName}
              items={column?.items}
              widthColumn={widthColumn}
            />
          ))}
      </DragDropContext>
    </PipelineContainer>
  )
}

PipelineComponent.propTypes = {
  valueData: PropTypes.array,
  categoryData: PropTypes.string,
  dataChart: PropTypes.array,
  pipelineColumns: PropTypes.array,

  onMoveElements: PropTypes.func,
  onAddColumn: PropTypes.func,
}

PipelineComponent.defaultProps = {
  valueData: [],
  categoryData: "",
  pipelineColumns: [],
  dataChart: [],

  onMoveElements: () => {},
  onAddColumn: () => {},
}

export default PipelineComponent
